import React, { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { Card, PageHeader, Btn, Input } from "../components/UI";
import { toast } from "sonner";
import { HandHeart, Plus, Trash2, Loader2, Phone, Mail } from "lucide-react";

const SKILLS = ["phone_banking", "canvassing", "events", "admin", "digital", "drivers", "signs"];

export default function Volunteers() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", skills: [], availability: "" });

  const load = () => api.get("/political-os/volunteers").then(r => setData(r.data)).catch(() => setData({ volunteers: [] }));
  useEffect(() => { load(); }, []);

  const toggleSkill = (s) => setForm(f => ({ ...f, skills: f.skills.includes(s) ? f.skills.filter(x => x !== s) : [...f.skills, s] }));

  const add = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try { await api.post("/political-os/volunteers", form); toast.success("Volunteer added"); setForm({ name: "", email: "", phone: "", skills: [], availability: "" }); load(); }
    catch (e) { toast.error(errMsg(e, "Could not add volunteer")); }
  };
  const remove = async (id) => { try { await api.delete(`/political-os/volunteers/${id}`); load(); } catch (e) { toast.error(errMsg(e)); } };

  if (!data) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="volunteers-page">
      <PageHeader eyebrow="Operations" title="Volunteers"
        subtitle="Opted-in volunteers with skills and availability — staff events, phone banks and literature drops." />
      <Card data-testid="add-volunteer-card">
        <div className="flex flex-wrap gap-3 items-end">
          <Input label="Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="min-w-[180px]" data-testid="volunteer-name-input" />
          <Input label="Email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="min-w-[180px]" />
          <Input label="Phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="w-36" />
          <Input label="Availability" value={form.availability} onChange={e => setForm(f => ({ ...f, availability: e.target.value }))} placeholder="Weekends" className="w-36" />
          <Btn onClick={add} data-testid="add-volunteer-btn"><Plus className="w-4 h-4" /> Add</Btn>
        </div>
        <div className="flex gap-1.5 flex-wrap mt-3">
          {SKILLS.map(s => (
            <button key={s} onClick={() => toggleSkill(s)}
              className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors ${form.skills.includes(s) ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-300" : "border-white/15 text-slate-500"}`}>
              {s.replace(/_/g, " ")}
            </button>
          ))}
        </div>
      </Card>
      <Card padded={false} data-testid="volunteers-list-card">
        <div className="divide-y divide-white/5">
          {data.volunteers.map(v => (
            <div key={v.id} className="px-5 py-3.5 flex items-center gap-4" data-testid={`volunteer-${v.id}`}>
              <HandHeart className="w-4 h-4 text-cyan-300/70 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm text-white">{v.name}</div>
                <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
                  {v.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{v.email}</span>}
                  {v.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{v.phone}</span>}
                  {v.availability && <span>{v.availability}</span>}
                </div>
              </div>
              <div className="flex gap-1 flex-wrap justify-end">
                {(v.skills || []).map(s => <span key={s} className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-white/15 text-slate-400">{s.replace(/_/g, " ")}</span>)}
              </div>
              <button onClick={() => remove(v.id)} className="text-slate-600 hover:text-red-400 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          {data.volunteers.length === 0 && <div className="px-5 py-6 text-sm text-slate-500">No volunteers yet.</div>}
        </div>
      </Card>
    </div>
  );
}
